<template>
  <AppLayout>
    <div class="fleet-crud-page">
      <div class="fleet-page-head">
        <div>
          <p class="kicker">Fleet / Trips</p>
          <h2>Trip Detail</h2>
        </div>
        <RouterLink class="btn-ghost" to="/fleet/trips">Back to Trips</RouterLink>
      </div>

      <div class="fleet-grid detail-grid">
        <section class="panel">
          <h3>Trip Snapshot</h3>
          <div v-if="loading">Loading...</div>
          <div v-else-if="!trip" class="empty">Trip not found.</div>
          <div v-else class="detail-list">
            <p><strong>Vehicle:</strong> {{ trip?.vehicle?.plateNumber || '-' }}</p>
            <p><strong>Driver:</strong> {{ trip?.driver?.user?.name || trip?.driver?.name || '-' }}</p>
            <p><strong>Origin:</strong> {{ trip?.requisition?.origin || '-' }}</p>
            <p><strong>Destination:</strong> {{ trip?.requisition?.destination || '-' }}</p>
            <p><strong>Start KMs:</strong> {{ trip.startKms ?? '-' }}</p>
            <p><strong>End KMs:</strong> {{ trip.endKms ?? '-' }}</p>
            <p><strong>Distance:</strong> {{ trip.totalDistance ?? '-' }}</p>
            <p><strong>Start Time:</strong> {{ formatDateTime(trip.startTime) }}</p>
            <p><strong>End Time:</strong> {{ trip.endTime ? formatDateTime(trip.endTime) : 'In Progress' }}</p>
          </div>
        </section>

        <div class="detail-side">
          <FleetTimeline title="Trip Timeline" :items="timelineItems" />
          <FleetAuditExport
            file-prefix="trip-audit"
            report-title="Trip Audit Report"
            :report-id="reportId"
            :reviewed-by="reviewedBy"
            :reviewed-date="reviewedDate"
            :summary-rows="summaryRows"
            :timeline-items="timelineItems"
          />
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue';
import FleetTimeline from './FleetTimeline.vue';
import FleetAuditExport from './FleetAuditExport.vue';
import fleetApi from '@/api/fleet';

export default {
  name: 'FleetTripDetail',
  components: { AppLayout, FleetTimeline, FleetAuditExport },
  data() {
    return {
      loading: false,
      trip: null,
    };
  },
  computed: {
    recordId() {
      return this.trip?.id || this.trip?.trip_id || this.$route.params.id;
    },
    reportId() {
      const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      return `FLT-TRP-${String(this.recordId)}-${datePart}`;
    },
    reviewedBy() {
      try {
        const user = JSON.parse(localStorage.getItem('user_cache') || 'null');
        return user?.name || 'System Reviewer';
      } catch (_) {
        return 'System Reviewer';
      }
    },
    reviewedDate() {
      return new Date().toLocaleDateString();
    },
    summaryRows() {
      if (!this.trip) return [];
      return [
        { label: 'Vehicle', value: this.trip?.vehicle?.plateNumber || '-' },
        { label: 'Driver', value: this.trip?.driver?.user?.name || this.trip?.driver?.name || '-' },
        { label: 'Origin', value: this.trip?.requisition?.origin || '-' },
        { label: 'Destination', value: this.trip?.requisition?.destination || '-' },
        { label: 'Start KMs', value: this.trip.startKms ?? '-' },
        { label: 'End KMs', value: this.trip.endKms ?? '-' },
        { label: 'Total Distance', value: this.trip.totalDistance ?? '-' },
      ];
    },
    timelineItems() {
      if (!this.trip) return [];
      const items = [
        {
          label: 'Trip started',
          time: this.formatDateTime(this.trip.startTime),
          note: `${this.trip?.requisition?.origin || '-'} -> ${this.trip?.requisition?.destination || '-'}`,
        },
      ];

      if (this.trip.endTime) {
        items.push({
          label: 'Trip completed',
          time: this.formatDateTime(this.trip.endTime),
          note: `Distance ${this.trip.totalDistance ?? '-'} km`,
        });
      } else {
        items.push({
          label: 'Trip currently active',
          time: 'Current',
          note: 'Awaiting completion update',
        });
      }

      return items;
    },
  },
  methods: {
    formatDateTime(v) {
      if (!v) return 'N/A';
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? 'N/A' : d.toLocaleString();
    },
    async loadDetail() {
      const id = this.$route.params.id;
      this.loading = true;
      try {
        const data = await fleetApi.listTrips({ page: 1, limit: 200 });
        const rows = fleetApi.unwrapList(data);
        const fromList = rows.find((r) => String(r.id || r.trip_id) === String(id));
        if (fromList) {
          this.trip = fromList;
          return;
        }

        const API_BASE_URL = (process.env.VUE_APP_URL || 'http://localhost:3000').replace(/\/+$/, '');
        const token = JSON.parse(localStorage.getItem('access_token') || 'null');
        const res = await fetch(`${API_BASE_URL}/trips/${id}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) throw new Error('Trip fetch failed');
        this.trip = await res.json();
      } catch (err) {
        this.$toast?.error?.(err?.message || 'Failed to load trip');
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.loadDetail();
  },
};
</script>

<style scoped>
@import './fleet.shared.css';

.detail-grid {
  grid-template-columns: 1fr 1fr;
}

.detail-side {
  display: grid;
  gap: 12px;
}

.detail-list p {
  margin: 6px 0;
  font-size: 13px;
}

@media (max-width: 1024px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
